/**
 * SDMS display rounding for NumericResult — ONE rule, shared by the SDMS Batch Results
 * popup and Result Tracking (task RTCFR, 2026-10-13; moved here unchanged from
 * SdmsBatchResultsPopup.tsx so both screens round the same way).
 *
 * OG display-rounds NumericResult to the row's Decimal places and trims trailing zeros
 * (e.g. 2.4972 -> 2.5, -0.0020 -> 0). Display only — the stored value is never changed.
 * A value that is not a number (e.g. "Not detected") and a blank value pass through as-is.
 *
 * @param value            the stored NumericResult text
 * @param decimals         the row's Decimal setting (SpreadSheetEntry_SampleInfo.[Decimal])
 * @param fallbackDecimals places to use when the row has no Decimal setting; `null` means
 *                         "show the value as stored" (Result Tracking). The Batch Results
 *                         popup keeps its original default of 2.
 * @returns the text to display
 */
export function formatSdmsNumeric(
  value: string | null | undefined,
  decimals: string | null | undefined,
  fallbackDecimals: number | null = 2,
): string {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const places = parseDecimals(decimals) ?? fallbackDecimals;
  if (places == null) return value;
  const rounded = Number(n.toFixed(places));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

/** Max places Number.prototype.toFixed accepts; a setting outside 0..100 is ignored. */
const MAX_TO_FIXED_DIGITS = 100;

/** The Decimal setting as a whole number of places, or null when it is blank or unusable. */
function parseDecimals(decimals: string | null | undefined): number | null {
  if (decimals == null || decimals.trim() === '' || !Number.isFinite(Number(decimals))) return null;
  const places = parseInt(decimals, 10);
  return places >= 0 && places <= MAX_TO_FIXED_DIGITS ? places : null;
}
